// Client credential and payment utilities

import { recordImpersonationAudit } from '@/lib/audit-client';

export interface GeneratedCredentials {
  username: string;
  password: string;
  clientCode: string;
  loginUrl: string;
}

export interface PaymentValidation {
  valid: boolean;
  error?: string;
}

export const generateClientCredentials = (companyName: string): GeneratedCredentials => {
  // Generate username from company name
  const cleanName = companyName
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')
    .substring(0, 15);
  const year = new Date().getFullYear();
  const username = `${cleanName}_${year}`;

  // Generate secure random password
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$%';
  const password = Array.from(
    { length: 12 },
    () => chars[Math.floor(Math.random() * chars.length)]
  ).join('');

  // Generate 6-character client code (letters + numbers)
  const codeChars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  const clientCode = Array.from(
    { length: 6 },
    () => codeChars[Math.floor(Math.random() * codeChars.length)]
  ).join('');

  return {
    username,
    password,
    clientCode: `VH-${clientCode}`,
    loginUrl: `${window.location.origin}/login`
  };
};

export const validatePaymentMethod = (paymentData: {
  cardNumber: string;
  expiry: string;
  cvv: string;
  cardholderName: string;
  zip: string;
}): PaymentValidation => {
  if (!paymentData.cardNumber || paymentData.cardNumber.replace(/\s/g, '').length < 13) {
    return { valid: false, error: 'Invalid card number' };
  }
  if (!paymentData.expiry || !/^\d{2}\/\d{2}$/.test(paymentData.expiry)) {
    return { valid: false, error: 'Invalid expiry (MM/YY)' };
  }
  if (!paymentData.cvv || paymentData.cvv.length < 3) {
    return { valid: false, error: 'Invalid CVV' };
  }
  if (!paymentData.cardholderName || paymentData.cardholderName.trim().length < 2) {
    return { valid: false, error: 'Cardholder name required' };
  }
  if (!paymentData.zip || paymentData.zip.length < 5) {
    return { valid: false, error: 'Invalid ZIP code' };
  }
  return { valid: true };
};

export const detectCardType = (cardNumber: string): 'Visa' | 'Mastercard' | 'Amex' | 'Discover' => {
  const num = cardNumber.replace(/\s/g, '');
  if (num.startsWith('4')) return 'Visa';
  if (/^5[1-5]/.test(num) || /^2[2-7]/.test(num)) return 'Mastercard';
  if (/^3[47]/.test(num)) return 'Amex';
  if (/^6(?:011|5)/.test(num)) return 'Discover';
  return 'Visa';
};

export const formatCardNumber = (value: string, cardType?: string): string => {
  const num = value.replace(/\D/g, '');
  const detectedType = cardType || detectCardType(num);
  
  // Amex uses 4-6-5 format (15 digits)
  if (detectedType === 'Amex') {
    const maxLen = 15;
    const trimmed = num.slice(0, maxLen);
    if (trimmed.length <= 4) return trimmed;
    if (trimmed.length <= 10) return trimmed.slice(0, 4) + ' ' + trimmed.slice(4);
    return trimmed.slice(0, 4) + ' ' + trimmed.slice(4, 10) + ' ' + trimmed.slice(10);
  }
  
  // Standard cards use 4-4-4-4 format (16 digits)
  const parts = [];
  for (let i = 0; i < num.length && i < 16; i += 4) {
    parts.push(num.slice(i, i + 4));
  }
  return parts.join(' ');
};

export const getCardPlaceholder = (cardNumber: string): string => {
  const cardType = detectCardType(cardNumber);
  if (cardType === 'Amex') {
    return '3782 822463 10005';
  }
  return '4242 4242 4242 4242';
};

export const getCvvLength = (cardNumber: string): number => {
  const cardType = detectCardType(cardNumber);
  return cardType === 'Amex' ? 4 : 3;
};

export const getCvvPlaceholder = (cardNumber: string): string => {
  const cardType = detectCardType(cardNumber);
  return cardType === 'Amex' ? '1234' : '123';
};

export const formatExpiry = (value: string): string => {
  const num = value.replace(/\D/g, '');
  if (num.length >= 2) {
    return num.slice(0, 2) + '/' + num.slice(2, 4);
  }
  return num;
};

// Impersonation helpers
export const startImpersonation = async (clientId: string, clientName: string, clientPackage: string, services: { demos: boolean }) => {
  sessionStorage.setItem('admin_impersonating', JSON.stringify({
    clientId,
    clientName,
    returnUrl: window.location.pathname
  }));
  localStorage.setItem('active_client_id', clientId);
  localStorage.setItem('client_package', clientPackage);
  localStorage.setItem('client_services', JSON.stringify(services));

  // Audit (FOUND-05) — await so the row lands before the hard redirect.
  await recordImpersonationAudit('start', clientId, { client_name: clientName });

  window.location.href = '/dashboard';
};

export const exitImpersonation = async () => {
  const impersonationData = sessionStorage.getItem('admin_impersonating');
  // Read clientId BEFORE clearing the session key so the audit row has a target.
  let clientId: string | null = null;
  if (impersonationData) {
    try { clientId = JSON.parse(impersonationData)?.clientId ?? null; } catch { /* ignore */ }
  }

  sessionStorage.removeItem('admin_impersonating');
  localStorage.removeItem('active_client_id');
  localStorage.removeItem('client_package');
  localStorage.removeItem('client_services');

  // Audit (FOUND-05) — await so the row lands before the hard redirect.
  if (clientId) {
    await recordImpersonationAudit('exit', clientId);
  }

  // Return to admin view
  window.location.href = '/admin/command-center';
};

export const getImpersonationData = (): { clientId: string; clientName: string; returnUrl: string } | null => {
  const data = sessionStorage.getItem('admin_impersonating');
  if (data) {
    try {
      return JSON.parse(data);
    } catch {
      return null;
    }
  }
  return null;
};

export const copyToClipboard = async (text: string): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
};

export const copyAllCredentials = (credentials: GeneratedCredentials): string => {
  return `Username: ${credentials.username}
Password: ${credentials.password}
Client Code: ${credentials.clientCode}
Login URL: ${credentials.loginUrl}`;
};
